import {
  isTerminalRunStatus,
  messageListResponseSchema,
  runListResponseSchema,
  runResponseSchema,
  sendMessageResponseSchema,
  type MessageListResponse,
  type RunListResponse,
  type RunResponse,
} from '@coworker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { useRecoverSession } from './use-recover-session.js';
import { ApiError } from '../lib/api-error.js';
import { useCurrentWorkspace } from './use-current-workspace.js';

/**
 * One thread's messages - API-008, WEB-007.
 *
 * Oldest first, and bounded: the API returns at most the most recent 200 and
 * there are no pagination parameters to send. API-017 owns the real thing.
 */
export const messagesQueryKey = (workspaceId: string | undefined, threadId: string | undefined) =>
  ['messages', workspaceId, threadId] as const;

export interface MessagesState {
  readonly query: UseQueryResult<MessageListResponse, unknown>;
  readonly isUnauthenticated: boolean;
}

/**
 * The run started by the last send, polled until it stops.
 *
 * **Polling and not streaming**, which is a decision rather than a limitation:
 * the answer arrives when the run finishes, and `POST` already returns the id
 * to watch. A streaming transport later replaces how this value arrives without
 * changing the response that carries it.
 *
 * `refetchInterval` is a function of the data so the poll stops itself, and
 * "finished" comes from `isTerminalRunStatus` in `@coworker/contracts` rather
 * than from a list written here: a client that re-derived it would disagree
 * with the server the first time a status was added. Not `isRunActive`, which
 * says the same thing in `@coworker/domain` - that package is `runtime:node`
 * and a browser may not import it.
 */
export const runQueryKey = (workspaceId: string | undefined, runId: string | undefined) =>
  ['run', workspaceId, runId] as const;

/** Two seconds while a run is active, as `aws/risks-and-tech-debt.md` prescribes. */
export const RUN_POLL_MS = 2_000;

export function useRun(runId: string | undefined): UseQueryResult<RunResponse, unknown> {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  return useQuery({
    queryKey: runQueryKey(workspace?.id, runId),
    enabled: status === 'signedIn' && workspace !== undefined && runId !== undefined,
    queryFn: () =>
      api.request(`/v1/runs/${runId ?? ''}`, {
        schema: runResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
    // **A failed read stops the poll.** `data === undefined` on its own reads
    // as "the first poll has not answered", which is also true of a 404 or a
    // 403 - and that polled every two seconds for as long as the tab was open.
    // The error is the thing that distinguishes them.
    retry: 1,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      const run = query.state.data?.run;
      if (run === undefined) return RUN_POLL_MS;
      return isTerminalRunStatus(run.status) ? false : RUN_POLL_MS;
    },
  });
}

export function useMessages(threadId: string | undefined): MessagesState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: messagesQueryKey(workspace?.id, threadId),
    enabled: status === 'signedIn' && workspace !== undefined && threadId !== undefined,
    queryFn: () =>
      api.request(`/v1/conversations/${threadId ?? ''}/messages`, {
        schema: messageListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated;

  // A lapsed token on this screen is recovered here, not only in `AppShell`.
  // This value was computed and returned and read by nobody, so a 401 on this
  // query dead-ended on an error card whose retry re-sent the same token.
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  return {
    query,
    isUnauthenticated,
  };
}

/**
 * Sends a message, and appends both rows the API answers with - **but only onto
 * a thread we have actually read.**
 *
 * The response carries the question and the reply, because the API persists them
 * in one transaction and returning one without the other would describe a state
 * the database never held. Appending them onto a loaded transcript is therefore
 * not optimism: it is the server's own answer, and it saves a second round trip.
 *
 * The `undefined` case is the one that matters, and a boundary review found it.
 * The composer stays live while the transcript is in `error`, which is reachable
 * on the first chat of the morning: the read 5xxs while the cluster resumes, the
 * query retries twice and gives up, and the send a few seconds later succeeds.
 * Writing those two rows into an empty cache would flip the query out of `error`
 * and render *the last exchange as the whole conversation*, with nothing to say
 * history was missing - and it would stay that way, because `staleTime` is 30s
 * and `refetchOnWindowFocus` is off. What we hold is the answer to one request,
 * not to the thread, so we ask for the thread.
 */
export function useSendMessage(threadId: string | undefined) {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (text: string) =>
      api.request(`/v1/conversations/${threadId ?? ''}/messages`, {
        method: 'POST',
        schema: sendMessageResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: { text },
      }),
    onSuccess: (sent) => {
      const key = messagesQueryKey(workspace?.id, threadId);
      const current = queryClient.getQueryData<MessageListResponse>(key);

      if (current === undefined) {
        void queryClient.invalidateQueries({ queryKey: key });
        return;
      }

      queryClient.setQueryData<MessageListResponse>(key, {
        messages: [...current.messages, ...sent.messages],
      });
    },
  });
}

/**
 * Whether this thread has a run still going - AGT-008.
 *
 * **What makes an approval survive a reload.** The run id lived only in React
 * state, so refreshing lost it; a run that answers in seconds could afford
 * that, and one parked on an approval could not - the card would be in the
 * thread with working buttons and nothing would notice when the run carried on.
 *
 * Fetched once per thread rather than polled: `useRun` does the polling, and
 * this only has to answer "what should it poll" when the screen first loads.
 * It refetches when the window regains focus, which is the case somebody
 * answering from another tab actually produces.
 */
export function useThreadRuns(
  threadId: string | undefined,
): UseQueryResult<RunListResponse, unknown> {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  return useQuery({
    queryKey: ['thread-runs', workspace?.id, threadId] as const,
    enabled: status === 'signedIn' && workspace !== undefined && threadId !== undefined,
    queryFn: () =>
      api.request(`/v1/conversations/${threadId ?? ''}/runs`, {
        schema: runListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });
}
